import {Injectable, NgZone} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {tryParseJson} from '../../utils';

export const APP_PREFIX = 'htk.';
export type StorageCallback = (newValue?: any, oldValue?: any, key?: string, event?: StorageEvent) => void;

@Injectable()
export class StorageService {

  private storageSupported = false;
  private listeners: {[key: string]: StorageCallback[]} = {};

  constructor(private Zone: NgZone) {
    try {
      const key = APP_PREFIX + 'test';
      localStorage.setItem(key, '1');
      localStorage.removeItem(key);
      this.storageSupported = true;
    } catch (e) {
      // Ignored.
    }
    if (this.storageSupported) {
      window.addEventListener('storage', (event: StorageEvent) => {
        if (event.key.indexOf(APP_PREFIX) !== 0) {
          return;
        }
        const key = event.key.substring(APP_PREFIX.length);
        const callbacks = this.listeners[key];
        if (callbacks) {
          const newValue = tryParseJson(event.newValue);
          const oldValue = tryParseJson(event.oldValue);
          Zone.run(() => {
            callbacks.forEach((callback) => {
              callback(newValue, oldValue, key, event);
            });
          });
        }
      });
    }
  }

  get<T>(key: string, defaultValue?: T): T {
    if (this.storageSupported) {
      const value = localStorage.getItem(APP_PREFIX + key);
      if (value !== null) {
        return tryParseJson(value);
      }
    }
    return defaultValue || null;
  }

  put(key: string, item: any): void {
    if (!this.storageSupported) {
      return;
    }
    if (typeof item !== 'string') {
      item = JSON.stringify(item);
    }
    localStorage.setItem(APP_PREFIX + key, item);
  }

  remove(key: string): void {
    if (this.storageSupported) {
      localStorage.removeItem(key);
    }
  }

  clear(): void {
    if (!this.storageSupported) {
      return;
    }
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; ++i) {
      const key = localStorage.key(i);
      if (key.indexOf(APP_PREFIX) === 0) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(localStorage.removeItem);
  }

  contains(key: string): boolean {
    return this.storageSupported
      ? localStorage.getItem(APP_PREFIX + key) !== null
      : false;
  }

  observe<T>(key: string, defaultValue?: T): Observable<T> {
    const subject = new BehaviorSubject<any>(this.get<T>(key, defaultValue));
    let callbacks = this.listeners[key];
    if (!callbacks) {
      callbacks = this.listeners[key] = [];
    }
    callbacks.push((val: T) => subject.next(val));
    return subject.asObservable();
  }
}
